import type { TFunction } from 'i18next';

import { Badge } from '@saas-kit/ui';
import type { DataTableColumn } from '@saas-kit/ui/DataTable';

import {
  NOTIFICATION_CHANNELS,
  NOTIFICATION_KINDS,
  NOTIFICATION_STATUSES,
  type NotificationRecord,
} from './api';

const STATUS_VARIANT: Record<
  string,
  'secondary' | 'success' | 'destructive' | 'outline'
> = {
  pending: 'secondary',
  sent: 'outline',
  read: 'success',
  failed: 'destructive',
  skipped: 'secondary',
};

interface ColumnOpts {
  onMarkRead: (row: NotificationRecord) => void;
  markPendingId: string | null;
}

function isMarkable(row: NotificationRecord): boolean {
  return row.channel === 'in_app' && row.status === 'sent';
}

export function buildInboxColumns(
  t: TFunction<'notifications'>,
  { onMarkRead, markPendingId }: ColumnOpts,
): DataTableColumn<NotificationRecord>[] {
  return [
    {
      id: 'kind',
      header: t('inbox.columns.kind'),
      cell: (row) => t(`kinds.${row.kind}`, { defaultValue: row.kind }),
      exportValue: (row) => row.kind,
      filter: {
        type: 'select',
        options: [
          { value: '', label: t('inbox.filters.all_kinds') },
          ...NOTIFICATION_KINDS.map((k) => ({
            value: k,
            label: t(`kinds.${k}`, { defaultValue: k }),
          })),
        ],
      },
    },
    {
      id: 'title',
      header: t('inbox.columns.title'),
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-foreground">{row.title}</p>
          <p className="truncate text-xs text-muted-foreground">{row.body}</p>
        </div>
      ),
      exportValue: (row) => row.title,
    },
    {
      id: 'channel',
      header: t('inbox.columns.channel'),
      cell: (row) =>
        t(`channels.${row.channel}`, { defaultValue: row.channel }),
      exportValue: (row) => row.channel,
      hideOnMobile: true,
      filter: {
        type: 'select',
        options: [
          { value: '', label: t('inbox.filters.all_channels') },
          ...NOTIFICATION_CHANNELS.map((c) => ({
            value: c,
            label: t(`channels.${c}`, { defaultValue: c }),
          })),
        ],
      },
    },
    {
      id: 'status',
      header: t('inbox.columns.status'),
      cell: (row) => (
        <Badge variant={STATUS_VARIANT[row.status] ?? 'secondary'}>
          {t(`inbox.status.${row.status}`, { defaultValue: row.status })}
        </Badge>
      ),
      exportValue: (row) => row.status,
      filter: {
        type: 'select',
        options: [
          { value: '', label: t('inbox.filters.all_statuses') },
          ...NOTIFICATION_STATUSES.map((s) => ({
            value: s,
            label: t(`inbox.status.${s}`, { defaultValue: s }),
          })),
        ],
      },
    },
    {
      id: 'created_at',
      header: t('inbox.columns.created_at'),
      cell: (row) => new Date(row.created_at).toLocaleString(),
      exportValue: (row) => row.created_at,
      hideOnMobile: true,
    },
    {
      id: 'actions',
      header: t('inbox.columns.actions'),
      align: 'end',
      exportSkip: true,
      cell: (row) =>
        isMarkable(row) ? (
          <button
            type="button"
            onClick={() => onMarkRead(row)}
            disabled={markPendingId === row.id}
            className="text-sm font-medium text-primary hover:underline disabled:opacity-50"
          >
            {t('inbox.actions.mark_read')}
          </button>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
  ];
}
